import { useMemo } from 'react';

import {
  ChartContainer,
  ChartGrid,
  ChartTooltip,
  Divider,
  Line,
  XAxis,
  YAxis,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';
import { ComputeResourceType } from '@cast/shared-types';
import { dashesOrNum, isE2E } from '@cast/utils';

import {
  buildAnalyzePrompt,
  useAnalyzeWithOpsPilot,
} from '@cast/features-organization-shared-opspilot';
import { HiddenDot } from 'components/chart';
import { attachTimeTicks } from 'utils/chart';

import {
  percentileToColor,
  percentileToTitle,
} from '../../../../_components/ChartSection/constants';
import { PreparedEvent } from '../../../../types';
import {
  SIMULATION_DAYS,
  generateSimulationMetrics,
} from './simulationMetrics';

const CHART_X_PADDING = 20;
const HIGHLIGHT_LEFT_MARGIN = 24;

// This component is used to highlight the area of the chart that corresponds to the look-back period.
const HighlightBox = ({ highlightHours }: { highlightHours?: number }) => {
  if (!highlightHours || highlightHours === 0) {
    return null;
  }

  const ratio = highlightHours / (SIMULATION_DAYS * 24);
  return (
    <Box
      position="absolute"
      width={`calc(${ratio * 100}% - ${2 * CHART_X_PADDING * ratio}px + ${
        CHART_X_PADDING + HIGHLIGHT_LEFT_MARGIN
      }px)`}
      height="84%"
      sx={{
        backgroundColor: tokens.theme.bg.structural.surface,
        borderStyle: 'solid',
        borderColor: tokens.theme.border.structural.neutralSubtle,
        borderWidth: 1,
        borderRadius: 1,
        boxShadow: tokens.theme.shadows.elevation.medium,
        ml: -HIGHLIGHT_LEFT_MARGIN,
        bottom: 24,
      }}
    />
  );
};

const LINE_KEYS = ['rec', 'max', 'min', 'p50'] as const;

type Props = {
  type: ComputeResourceType;
  highlightTimestamp?: string;
  event?: PreparedEvent;
  testId: string;
  seed: string;
  /**
   * Recommendation percentile as a fraction (0..1). When omitted no `rec` line
   * is drawn (used by the look-back period simulation, which doesn't affect the
   * recommendation value directly).
   */
  percentile?: number;
  /** Overhead as a fraction (0..1). Defaults to 0. */
  overhead?: number;
  highlightHours?: number;
};

export const ResourcesSimulationChart = ({
  type,
  testId,
  seed,
  percentile,
  overhead,
  highlightHours,
}: Props) => {
  const analyzeWithOpsPilot = useAnalyzeWithOpsPilot({
    buildPrompt: buildAnalyzePrompt({
      context: 'resource simulation',
      getDefaultRange: undefined,
    }),
  });

  const metrics = useMemo(
    () => generateSimulationMetrics({ seed, percentile, overhead }),
    [seed, percentile, overhead]
  );

  const chartData = useMemo(() => attachTimeTicks(metrics), [metrics]);

  const unit = type === 'cpu' ? 'CPU' : 'GiB';

  return (
    <Stack data-testid={testId}>
      <Typography color={tokens.theme.fg.structural.secondary} variant="A2">
        {unit}
      </Typography>
      <Divider
        sx={{ my: 4, opacity: 0.5 }}
        color={tokens.theme.border.structural.neutralSubtle}
      />
      <Box
        width="calc(100%)"
        height={148}
        position="relative"
        data-testid="chart"
      >
        <HighlightBox highlightHours={highlightHours} />
        <ChartContainer
          data={chartData}
          syncId="workload-simulation-metrics"
          sx={{ backgroundColor: 'transparent' }}
          analyzeWithOpsPilot={analyzeWithOpsPilot}
        >
          <ChartGrid hideVertical />

          {LINE_KEYS.map((key) => (
            <Line
              key={key}
              dataKey={key}
              stroke={percentileToColor[key]}
              strokeWidth={1}
              strokeDasharray={key === 'rec' ? '6 3' : undefined}
              dot={isE2E() ? HiddenDot : false}
              tooltipConfig={{
                displayName: percentileToTitle[key],
                unit,
              }}
            />
          ))}

          <XAxis
            dataKey="chartTimestampUnix"
            tickFormatter={(tick, index) => {
              if (!index) return ' ';
              if (index === 8) return `${index} days`;
              return `${index}`;
            }}
            interval={2}
            tickMargin={8}
            domain={['auto', 'auto']}
          />

          <YAxis
            unitFormatter={(v) => `${v} ${unit}`}
            // Floor at 0; let recharts auto-pick the upper bound so overhead > 100% renders above the historical 0.3 cap.
            domain={[0, 'auto']}
          />

          <ChartTooltip
            testId="data-popper"
            formatValue={(value) => dashesOrNum({ value, roundBy: 3 })}
          />
        </ChartContainer>
      </Box>
    </Stack>
  );
};
